import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { useTranslation } from 'react-i18next';
import { toast } from 'sonner';
import {
  getInvoices,
  getInvoice,
  getInvoiceV4,
  createInvoice,
  updateInvoice,
  deleteInvoice,
  uploadInvoicePoFile,
  deleteInvoicePoFile,
  addInvoiceNote,
  getInvoiceHtml,
  getCustomerOrganisations,
  getCustomerOrganisation,
  getSalesUsers,
  getSubscriptionsByCustomer,
  getOrganisationUsers,
  getQuotesByAccount,
  getProjectsByAccount,
  getInvoiceDocuments,
  addInvoiceDocument,
  removeInvoiceDocument,
  getInvoiceActivities,
  addInvoiceActivity,
  addSensorToInvoice,
  removeSensorFromInvoice,
  getContractById,
} from '../api';
import type { ContractDoc } from '../api';
import { getSensorsByInvoices } from '@/features/quotes/api';
import type { QuoteSensor } from '@/features/quotes/types';
import { transformV4InvoiceToV3 } from '../types';
import { invoiceKeys } from './queryKeys';
import type {
  InvoicesFilters,
  CreateInvoiceInput,
  UpdateInvoiceInput,
  AddInvoiceNoteInput,
} from '../types';

/**
 * Helper to extract error message from unknown error
 */
function getErrorMessage(error: unknown): string {
  if (error instanceof Error) return error.message;
  if (typeof error === 'object' && error !== null && 'message' in error) {
    return String((error as { message: unknown }).message);
  }
  return 'An unexpected error occurred';
}

// =============================================================================
// Query Hooks
// =============================================================================

/**
 * Hook to fetch invoices with filters
 */
export function useInvoices(filters: InvoicesFilters = {}) {
  return useQuery({
    queryKey: invoiceKeys.list(filters),
    queryFn: () => getInvoices(filters),
  });
}

// MongoDB ObjectIds are 24 hex characters
const isMongoId = (id: string) => /^[a-f\d]{24}$/i.test(id);

/**
 * Hook to fetch a single invoice by ID.
 * Detects whether the ID is a MongoDB ObjectId or PG UUID and routes accordingly.
 * This prevents sending a PG UUID to the V3 MongoDB endpoint (which would 404).
 */
export function useInvoice(invoiceId: string | undefined) {
  return useQuery({
    queryKey: invoiceKeys.detail(invoiceId!),
    queryFn: async () => {
      if (isMongoId(invoiceId!)) {
        return getInvoice(invoiceId!);
      }
      // PG UUID — use V4 endpoint and transform to V3 shape
      const v4 = await getInvoiceV4(invoiceId!);
      return transformV4InvoiceToV3(v4);
    },
    enabled: !!invoiceId,
  });
}

/**
 * Hook to fetch invoice HTML for viewing/printing
 */
export function useInvoiceHtml(invoiceId: string | undefined, enabled = false) {
  return useQuery({
    queryKey: invoiceKeys.html(invoiceId!),
    queryFn: () => getInvoiceHtml(invoiceId!),
    enabled: !!invoiceId && enabled,
  });
}

/**
 * Hook to fetch customer organisations for filter dropdown
 */
export function useCustomerOrganisations() {
  return useQuery({
    queryKey: invoiceKeys.customers(),
    queryFn: getCustomerOrganisations,
    staleTime: 5 * 60 * 1000, // Cache for 5 minutes
  });
}

/**
 * Hook to fetch a single customer account detail, including the Mongo organisation id
 * needed for V3 invoice writes.
 */
export function useCustomerOrganisation(accountId: string) {
  return useQuery({
    queryKey: invoiceKeys.customer(accountId),
    queryFn: () => getCustomerOrganisation(accountId),
    enabled: !!accountId,
    staleTime: 5 * 60 * 1000,
  });
}

/**
 * Hook to fetch sales users for filter dropdown
 */
export function useSalesUsers() {
  return useQuery({
    queryKey: invoiceKeys.salesUsers(),
    queryFn: getSalesUsers,
    staleTime: 5 * 60 * 1000, // Cache for 5 minutes
  });
}

/**
 * Hook to fetch subscriptions (V4 contracts) filtered by customer.
 * Pass pgAccountId when available for server-side filtering.
 */
export function useSubscriptionsByCustomer(customerId: string, pgAccountId?: string) {
  return useQuery({
    queryKey: invoiceKeys.contracts(pgAccountId || customerId),
    queryFn: () => getSubscriptionsByCustomer(customerId, pgAccountId),
    enabled: !!customerId || !!pgAccountId,
    staleTime: 5 * 60 * 1000,
  });
}

/** @deprecated Use useSubscriptionsByCustomer instead */
export function useContractsByCustomer(customerId: string, pgAccountId?: string) {
  return useSubscriptionsByCustomer(customerId, pgAccountId);
}

/**
 * Fetch the full Mongo contract document by its Mongo ObjectId.
 * Enabled only when a contractId is provided.
 * Used by the "Fill from subscription" button in InvoiceFormModal.
 */
export function useContractById(contractId?: string) {
  return useQuery({
    queryKey: invoiceKeys.contractDoc(contractId!),
    queryFn: () => getContractById(contractId!),
    enabled: !!contractId,
    staleTime: 5 * 60 * 1000,
  });
}

export type { ContractDoc };

/**
 * Hook to fetch users belonging to a specific account (V4 PostgreSQL)
 */
export function useOrganisationUsers(accountId: string) {
  return useQuery({
    queryKey: invoiceKeys.organisationUsers(accountId),
    queryFn: () => getOrganisationUsers(accountId),
    enabled: !!accountId,
    staleTime: 5 * 60 * 1000,
  });
}

/**
 * Hook to fetch operations projects assigned to an account (V4 PostgreSQL)
 */
export function useProjectsByAccount(accountId: string) {
  return useQuery({
    queryKey: invoiceKeys.projects(accountId),
    queryFn: () => getProjectsByAccount(accountId),
    enabled: !!accountId,
    staleTime: 5 * 60 * 1000,
  });
}

/**
 * Hook to fetch quotes assigned to an account (V4 PostgreSQL)
 */
export function useQuotesByAccount(accountId: string) {
  return useQuery({
    queryKey: invoiceKeys.quotes(accountId),
    queryFn: () => getQuotesByAccount(accountId),
    enabled: !!accountId,
    staleTime: 5 * 60 * 1000,
  });
}

// =============================================================================
// Mutation Hooks
// =============================================================================

/**
 * Hook to create a new invoice
 */
export function useCreateInvoice() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (data: CreateInvoiceInput) => createInvoice(data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: invoiceKeys.lists() });
      toast.success('Invoice created successfully');
    },
    onError: (error) => {
      toast.error('Failed to create invoice', { description: getErrorMessage(error) });
    },
  });
}

/**
 * Hook to update an invoice
 */
export function useUpdateInvoice() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({ invoiceId, data }: { invoiceId: string; data: UpdateInvoiceInput }) =>
      updateInvoice(invoiceId, data),
    onSuccess: (_invoice, variables) => {
      queryClient.invalidateQueries({ queryKey: invoiceKeys.lists() });
      queryClient.invalidateQueries({ queryKey: invoiceKeys.detail(variables.invoiceId) });
      toast.success('Invoice updated successfully');
    },
    onError: (error) => {
      toast.error('Failed to update invoice', { description: getErrorMessage(error) });
    },
  });
}

/**
 * Hook to delete an invoice
 */
export function useDeleteInvoice() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (invoiceId: string) => deleteInvoice(invoiceId),
    onSuccess: (_result, invoiceId) => {
      queryClient.invalidateQueries({ queryKey: invoiceKeys.lists() });
      queryClient.invalidateQueries({ queryKey: invoiceKeys.detail(invoiceId) });
      toast.success('Invoice deleted successfully');
    },
    onError: (error) => {
      toast.error('Failed to delete invoice', { description: getErrorMessage(error) });
    },
  });
}

/**
 * Hook to upload a PO (purchase order) file and attach it to an invoice.
 * The invoice list/detail caches are refreshed so the attached file link
 * appears without reopening the modal.
 */
export function useUploadInvoicePoFile() {
  const queryClient = useQueryClient();
  const { t } = useTranslation('invoices');

  return useMutation({
    mutationFn: ({ invoiceId, file }: { invoiceId: string; file: File }) =>
      uploadInvoicePoFile(invoiceId, file),
    onSuccess: (_invoice, variables) => {
      queryClient.invalidateQueries({ queryKey: invoiceKeys.lists() });
      queryClient.invalidateQueries({ queryKey: invoiceKeys.detail(variables.invoiceId) });
      toast.success(t('toasts.poFileUploaded', 'PO file uploaded'));
    },
    onError: (error) => {
      toast.error(t('toasts.poFileUploadFailed', 'Failed to upload PO file'), {
        description: getErrorMessage(error),
      });
    },
  });
}

/**
 * Hook to detach the PO file from an invoice.
 */
export function useDeleteInvoicePoFile() {
  const queryClient = useQueryClient();
  const { t } = useTranslation('invoices');

  return useMutation({
    mutationFn: (invoiceId: string) => deleteInvoicePoFile(invoiceId),
    onSuccess: (_invoice, invoiceId) => {
      queryClient.invalidateQueries({ queryKey: invoiceKeys.lists() });
      queryClient.invalidateQueries({ queryKey: invoiceKeys.detail(invoiceId) });
      toast.success(t('toasts.poFileRemoved', 'PO file removed'));
    },
    onError: (error) => {
      toast.error(t('toasts.poFileRemoveFailed', 'Failed to remove PO file'), {
        description: getErrorMessage(error),
      });
    },
  });
}

/**
 * Hook to add a note to an invoice
 */
export function useAddInvoiceNote() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({ invoiceId, data }: { invoiceId: string; data: AddInvoiceNoteInput }) =>
      addInvoiceNote(invoiceId, data),
    onSuccess: (_invoice, variables) => {
      queryClient.invalidateQueries({ queryKey: invoiceKeys.detail(variables.invoiceId) });
      toast.success('Note added successfully');
    },
    onError: (error) => {
      toast.error('Failed to add note', { description: getErrorMessage(error) });
    },
  });
}

// =============================================================================
// Invoice Documents (DMS links)
// =============================================================================

/**
 * Hook to get documents linked to an invoice
 */
export function useInvoiceDocuments(invoiceId: string | undefined) {
  return useQuery({
    queryKey: invoiceKeys.documents(invoiceId || ''),
    queryFn: () => getInvoiceDocuments(invoiceId!),
    enabled: !!invoiceId,
  });
}

/**
 * Hook to link a DMS document to an invoice
 */
export function useAddInvoiceDocument() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({ invoiceId, documentId }: { invoiceId: string; documentId: string }) =>
      addInvoiceDocument(invoiceId, documentId),
    onSuccess: (_data, variables) => {
      queryClient.invalidateQueries({ queryKey: invoiceKeys.documents(variables.invoiceId) });
      queryClient.invalidateQueries({ queryKey: invoiceKeys.lists() });
      queryClient.invalidateQueries({ queryKey: ['atek-invoices-sync'] });
      queryClient.invalidateQueries({ queryKey: ['atek-invoices'] });
      toast.success('Document linked');
    },
    onError: (error) => {
      toast.error('Failed to link document', { description: getErrorMessage(error) });
    },
  });
}

/**
 * Hook to unlink a DMS document from an invoice
 */
export function useRemoveInvoiceDocument() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({ invoiceId, documentId }: { invoiceId: string; documentId: string }) =>
      removeInvoiceDocument(invoiceId, documentId),
    onSuccess: (_data, variables) => {
      queryClient.invalidateQueries({ queryKey: invoiceKeys.documents(variables.invoiceId) });
      queryClient.invalidateQueries({ queryKey: invoiceKeys.lists() });
      queryClient.invalidateQueries({ queryKey: ['atek-invoices-sync'] });
      queryClient.invalidateQueries({ queryKey: ['atek-invoices'] });
      toast.success('Document unlinked');
    },
    onError: (error) => {
      toast.error('Failed to unlink document', { description: getErrorMessage(error) });
    },
  });
}

// =============================================================================
// Invoice Activities (engagement / audit timeline)
// =============================================================================

/** Fetch the activity timeline for an invoice. */
export function useInvoiceActivities(invoiceId: string | undefined) {
  return useQuery({
    queryKey: invoiceKeys.activities(invoiceId || ''),
    queryFn: () => getInvoiceActivities(invoiceId!),
    enabled: !!invoiceId,
  });
}

/** Append a manual note/comment to an invoice's timeline. */
export function useAddInvoiceActivity() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: ({ invoiceId, activityType, description, metadata }: {
      invoiceId: string;
      activityType: string;
      description: string;
      metadata?: Record<string, unknown>;
    }) => addInvoiceActivity(invoiceId, { activityType, description, metadata }),
    onSuccess: (_data, variables) => {
      queryClient.invalidateQueries({ queryKey: invoiceKeys.activities(variables.invoiceId) });
      toast.success('Note added');
    },
    onError: (error) => {
      toast.error('Failed to add note', { description: getErrorMessage(error) });
    },
  });
}

// =============================================================================
// Invoice Sensors Hooks
// =============================================================================

/**
 * Fetch sensors linked to an invoice by invoice_number.
 * Delegates to the shared getSensorsByInvoices api (quotes feature).
 * `organisation` (Mongo org id of the invoice's customer) scopes the query —
 * getSensorsByInvoices must never receive 'all'.
 */
export function useInvoiceSensors(
  invoiceNumber: string | undefined,
  organisation?: string,
): {
  sensors: QuoteSensor[];
  isLoading: boolean;
} {
  const query = useQuery({
    queryKey: invoiceKeys.sensors(invoiceNumber || ''),
    queryFn: () => getSensorsByInvoices([invoiceNumber!], organisation),
    enabled: !!invoiceNumber,
    staleTime: 2 * 60 * 1000,
  });

  return {
    sensors: Array.isArray(query.data) ? query.data : [],
    isLoading: query.isLoading,
  };
}

/**
 * Mutation: link a sensor to an invoice (POST add-sensor).
 * Invalidates the invoice-sensors query on success.
 */
export function useAddSensorToInvoice(invoiceNumber: string | undefined) {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: ({ invoiceId, sensorId }: { invoiceId: string; sensorId: string }) =>
      addSensorToInvoice(invoiceId, sensorId),
    onSuccess: () => {
      if (invoiceNumber) {
        queryClient.invalidateQueries({ queryKey: invoiceKeys.sensors(invoiceNumber) });
      }
      toast.success('Sensor linked to invoice');
    },
    onError: (error) => {
      toast.error('Failed to link sensor', { description: getErrorMessage(error) });
    },
  });
}

/**
 * Mutation: unlink a sensor from an invoice (POST remove-sensor).
 * Invalidates the invoice-sensors query on success.
 */
export function useRemoveSensorFromInvoice(invoiceNumber: string | undefined) {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: ({ invoiceId, sensorId }: { invoiceId: string; sensorId: string }) =>
      removeSensorFromInvoice(invoiceId, sensorId),
    onSuccess: () => {
      if (invoiceNumber) {
        queryClient.invalidateQueries({ queryKey: invoiceKeys.sensors(invoiceNumber) });
      }
      toast.success('Sensor unlinked from invoice');
    },
    onError: (error) => {
      toast.error('Failed to unlink sensor', { description: getErrorMessage(error) });
    },
  });
}
